/* Créé par Incipit Studio — Navigation KAARZ */
if (!customElements.get('kaarz-header')) {
  customElements.define('kaarz-header', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.events = { signal: this.controller.signal };
      this.bar = this.querySelector('.kn-bar');
      this.mega = this.querySelector('.kn-mega');
      this.dialog = this.querySelector('dialog');
      this.triggers = [...this.querySelectorAll('[data-menu-trigger]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.screens = [...this.querySelectorAll('[data-screen]')];
      this.mobileButton = this.querySelector('[data-mobile-open]');
      this.backButton = this.querySelector('[data-mobile-back]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 1200px)');
      this.history = [];
      this.pinned = false;
      document.addEventListener('keydown', event => {
        if (['Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key)) this.classList.add('is-keyboard');
      }, { ...this.events, capture: true });
      document.addEventListener('pointerdown', () => this.classList.remove('is-keyboard'), { ...this.events, capture: true });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && this.desktop.matches && !this.classList.contains('is-compact')) this.classList.add('is-solid');
      }, this.events);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse' || this.pinned) return;
        this.closeTimer = setTimeout(() => this.closeMenu(), 100);
      }, this.events);
      this.addEventListener('pointerover', () => clearTimeout(this.closeTimer), this.events);
      this.triggers.forEach(trigger => {
        trigger.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.openMenu(trigger.dataset.menuTrigger);
        }, this.events);
        trigger.addEventListener('click', () => {
          if (trigger.getAttribute('aria-expanded') === 'true' && this.clickedMenu === trigger.dataset.menuTrigger) {
            this.closeMenu();
          } else {
            this.openMenu(trigger.dataset.menuTrigger);
            this.clickedMenu = trigger.dataset.menuTrigger;
          }
        }, this.events);
        trigger.addEventListener('keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.openMenu(trigger.dataset.menuTrigger);
            this.panels.find(panel => panel.dataset.panel === trigger.dataset.menuTrigger)?.querySelector('a')?.focus();
          }
        }, this.events);
      });
      this.querySelectorAll('[data-direct-link]').forEach(link => {
        link.addEventListener('pointerenter', () => this.closeMenu(true), this.events);
        link.addEventListener('focus', () => this.closeMenu(true), this.events);
      });
      this.panels.forEach(panel => panel.querySelectorAll('[data-collection]').forEach(link => {
        const select = () => this.selectCollection(panel, link.dataset.collection);
        link.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') select(); }, this.events);
        link.addEventListener('focus', select, this.events);
      }));
      this.addEventListener('focusout', () => {
        queueMicrotask(() => { if (!this.contains(document.activeElement) && !this.pinned) this.closeMenu(); });
      }, this.events);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target) && !this.pinned) this.closeMenu();
      }, this.events);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.dialog.open) {
          const trigger = this.triggers.find(item => item.getAttribute('aria-expanded') === 'true');
          this.pinned = false;
          this.closeMenu();
          trigger?.focus();
        }
      }, this.events);
      this.mobileButton.addEventListener('click', () => this.openMobile(), this.events);
      this.querySelector('[data-mobile-close]').addEventListener('click', () => this.closeMobile(), this.events);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeMobile(); }, this.events);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.restoreScroll(); }, this.events);
      this.backButton.addEventListener('click', () => {
        const entry = this.history.pop();
        if (entry) { this.showScreen(entry.id); entry.trigger?.focus(); }
      }, this.events);
      this.querySelectorAll('[data-screen-open]').forEach(button => button.addEventListener('click', () => {
        this.history.push({ id: this.screenId, trigger: button });
        this.showScreen(button.dataset.screenOpen);
        this.backButton.focus({ preventScroll: true });
      }, this.events));
      this.motion.addEventListener('change', () => this.updateVideos(), this.events);
      document.addEventListener('visibilitychange', () => this.updateVideos(), this.events);
      document.addEventListener('shopify:block:select', event => this.selectEditorBlock(event), this.events);
      document.addEventListener('shopify:block:deselect', () => { this.pinned = false; }, this.events);
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.bar);
      document.fonts?.ready.then(() => { if (this.isConnected) this.resize(); });
      this.resize();
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.closeMobile(false);
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    resize() {
      const wasCompact = this.classList.contains('is-compact');
      this.classList.remove('is-compact');
      const nav = this.querySelector('.kn-desktop-nav');
      const logo = this.querySelector('.kn-logo');
      const collision = this.desktop.matches && nav.getBoundingClientRect().right + 24 > logo.getBoundingClientRect().left;
      this.classList.toggle('is-compact', collision);
      if (!this.desktop.matches || collision) this.closeMenu();
      else if (this.dialog.open) this.closeMobile();
      if (wasCompact !== collision && !this.desktop.matches) this.closeMenu();
      this.style.setProperty('--kn-menu-top', `${Math.max(0, this.bar.getBoundingClientRect().bottom)}px`);
    }
    openMenu(id) {
      if (!this.desktop.matches || this.classList.contains('is-compact')) return;
      clearTimeout(this.closeTimer);
      this.clickedMenu = null;
      this.classList.add('is-solid');
      this.mega.hidden = false;
      this.style.setProperty('--kn-menu-top', `${Math.max(0, this.bar.getBoundingClientRect().bottom)}px`);
      this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', String(trigger.dataset.menuTrigger === id)));
      this.panels.forEach(panel => {
        const active = panel.dataset.panel === id;
        panel.classList.toggle('is-active', active);
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.updateVideos();
    }
    closeMenu(keepWhite = false) {
      if (!this.mega) return;
      clearTimeout(this.closeTimer);
      this.mega.hidden = true;
      this.clickedMenu = null;
      this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      this.panels.forEach(panel => { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); panel.classList.remove('is-active'); });
      this.classList.toggle('is-solid', keepWhite);
      this.updateVideos();
    }
    selectCollection(panel, index) {
      panel.querySelectorAll('[data-collection]').forEach(link => link.classList.toggle('is-selected', link.dataset.collection === index));
      panel.querySelectorAll('[data-products]').forEach(list => {
        const selected = list.dataset.products === index;
        list.classList.toggle('is-selected', selected);
        list.inert = !selected;
        list.setAttribute('aria-hidden', String(!selected));
      });
    }
    updateVideos() {
      this.querySelectorAll('video').forEach(video => {
        const visible = video.closest('.kn-panel')?.classList.contains('is-active') && !this.mega.hidden;
        if (visible && !document.hidden && !this.motion.matches) video.play()?.catch(() => {});
        else video.pause();
      });
    }
    openMobile() {
      if (this.dialog.open) return;
      this.closeMenu();
      this.history = [];
      this.showScreen('root');
      this.scrollState = { y: window.scrollY, position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollState.y}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.mobileButton.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-mobile-close]').focus({ preventScroll: true });
    }
    showScreen(id) {
      const current = this.screens.find(screen => screen.dataset.screen === id);
      if (!current) return;
      this.screenId = id;
      this.screens.forEach(screen => { screen.hidden = screen !== current; });
      this.backButton.hidden = this.history.length === 0;
      this.dialog.scrollTop = 0;
      const color = getComputedStyle(current.querySelector('.kn-banner')).getPropertyValue('--kn-banner-color');
      this.querySelector('.kn-mobile-tools').style.color = color;
    }
    closeMobile(restoreFocus = true) {
      if (!this.dialog) return;
      const wasOpen = this.dialog.open;
      if (wasOpen) {
        this.closeMenu();
        this.dialog.close();
      }
      this.restoreScroll();
      this.mobileButton?.setAttribute('aria-expanded', 'false');
      if (wasOpen && restoreFocus && this.isConnected) this.mobileButton.focus({ preventScroll: true });
    }
    restoreScroll() {
      if (!this.scrollState) return;
      const state = this.scrollState;
      this.scrollState = null;
      Object.assign(document.body.style, { position: state.position, top: state.top, width: state.width, overflow: state.overflow });
      window.scrollTo({ top: state.y, behavior: 'instant' });
    }
    selectEditorBlock(event) {
      const target = event.target;
      if (!this.contains(target)) return;
      this.pinned = true;
      const panel = target.closest('[data-panel]');
      const menuBlock = target.closest('[data-menu-block]');
      const id = panel?.dataset.panel || menuBlock?.dataset.menuBlock;
      if (!id) return;
      if (this.desktop.matches && !this.classList.contains('is-compact')) {
        this.openMenu(id);
        const collection = target.closest('[data-collection]');
        if (collection && panel) this.selectCollection(panel, collection.dataset.collection);
      }
      else {
        this.openMobile();
        if (this.screens.some(screen => screen.dataset.screen === `menu-${id}`)) {
          this.history.push({ id: 'root', trigger: this.querySelector(`[data-mobile-block="${id}"]`) });
          this.showScreen(`menu-${id}`);
        }
      }
    }
  });
}
